import axios from "axios";
import React, { useCallback } from "react";
import { useState } from "react";
import { useEffect } from "react";
import { KeyboardEvent } from "react";
import { useRef } from "react";

import Box from "@mui/material/Box";

import Collapse from "@mui/material/Collapse";

import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import FormControl from "@mui/material/FormControl";
import { FormControlLabel } from "@mui/material";
import { Checkbox } from "@mui/material";
import TextField from "@mui/material/TextField";

import { Typography } from "@mui/material";

import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";

import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockResetIcon from "@mui/icons-material/LockReset";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";

import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Link from "@mui/material/Link";

import GetCSRFToken from "./GetCSRFToken";
import PagePaper from "../PageComponents/PagePaper";
import { isMobile } from "react-device-detect";
import PersonalizationConfig from "../PersonalizationComponents/PersonalizationConfig";

const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,10}$/;
// TODO: replace email regex with a library

const RedirectSecondsConst = 10;

type RegisterOrResetDialogProps = {
  isReset: boolean;
  openLoginDialog: () => void;
  openRegisterDialog: () => void;
  setUserFullName: (newName: string | undefined) => void;
  setUserConfig: (newConfig: PersonalizationConfig) => void;
  username: string;
  setUsername: (newUsername: string) => void;
  email: string;
  setEmail: (newEmail: string) => void;
  emailUnsubmitted: boolean;
  setEmailUnsubmitted: (newEmailUnsubmitted: boolean) => void;
  setEmailVerified: (newEmailVerified: boolean) => void;
  registrationEnabled: boolean;
};

function RegisterOrResetDialog(props: RegisterOrResetDialogProps) {
  const navigate = useNavigate();

  const [emailErrorMessage, setEmailErrorMessage] = useState<String | undefined>(undefined);

  const [usernameErrorMessage, setUsernameErrorMessage] = useState<String | undefined>(undefined);

  const [passwordRequirementsMessage, setPasswordRequirementsMessage] = useState<String | undefined>(undefined);

  const [password, setPassword] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [serverUserName, setServerUserName] = useState<string | undefined>(undefined);
  const [serverConfig, setServerConfig] = useState<PersonalizationConfig | undefined>(undefined);

  const [tosChecked, setTosChecked] = useState(false);

  const [verifyCode, setVerifyCode] = useState("");

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [emailVerifySuccess, setEmailVerifySuccess] = useState(false);
  const [verifyCodeErrorMessage, setVerifyCodeErrorMessage] = useState<String | undefined>(undefined);

  const [redirectSeconds, setRedirectSeconds] = useState(RedirectSecondsConst);
  const [redirectTimeout, setRedirectTimeout] = useState<NodeJS.Timeout | undefined>(undefined);
  const [countDownInterval, setCountDownInterval] = useState<NodeJS.Timeout | undefined>(undefined);

  const promptEmailVerify = !props.emailUnsubmitted && !props.isReset && success;

  const usernameTextFieldRef = useRef<HTMLDivElement>(null);
  const emailTextFieldRef = useRef<HTMLDivElement>(null);
  const emailVerifyCodeTextFieldRef = useRef<HTMLDivElement>(null);

  const setEmail = props.setEmail;
  const setUsername = props.setUsername;
  const setEmailUnsubmitted = props.setEmailUnsubmitted;

  // Clear the fields after the dialog goes away
  const clearFieldsDelayed = useCallback(() => {
    setTimeout(() => {
      setEmail("");
      setUsername("");
      setPassword("");
      setFirstName("");
      setLastName("");
      setVerifyCode("");
      setSuccess(false);
      setUsernameErrorMessage(undefined);
      setPasswordRequirementsMessage(undefined);
      setVerifyCodeErrorMessage(undefined);
      setEmailUnsubmitted(true);
    }, 200);
  }, [
    setEmail,
    setUsername,
    setPassword,
    setFirstName,
    setLastName,
    setSuccess,
    setVerifyCode,
    setUsernameErrorMessage,
    setPasswordRequirementsMessage,
    setVerifyCodeErrorMessage,
    setEmailUnsubmitted,
  ]);

  const handleCancel = useCallback(() => {
    if (!loading) {
      setPassword("");
      setLoading(false);
      setUsernameErrorMessage(undefined);
      setPasswordRequirementsMessage(undefined);
      setVerifyCodeErrorMessage(undefined);
      setEmailUnsubmitted(true);
    }
  }, [
    setPassword,
    setLoading,
    setUsernameErrorMessage,
    setPasswordRequirementsMessage,
    setEmailUnsubmitted,
    setVerifyCodeErrorMessage,
    loading,
  ]);

  function catchEnterKeyDownToSubmit(e: KeyboardEvent) {
    if (FormFullyFilled && e.key === "Enter") {
      handleSubmit();
    }
  }

  const setUserFullName = props.setUserFullName;
  const setUserConfig = props.setUserConfig;

  const cleanUp = useCallback(() => {
    setUserFullName(serverUserName);
    if (serverConfig !== undefined) {
      setUserConfig(serverConfig);
    }
    if (serverUserName !== undefined) {
      navigate("/stock-plans");
    } else {
      navigate("/");
    }
    clearFieldsDelayed();
    if (countDownInterval) {
      clearInterval(countDownInterval);
    }
    setCountDownInterval(undefined);
    setRedirectSeconds(RedirectSecondsConst);
  }, [
    navigate,
    clearFieldsDelayed,
    countDownInterval,
    setCountDownInterval,
    setRedirectSeconds,
    serverUserName,
    serverConfig,
    setUserFullName,
    setUserConfig,
  ]);

  const FormFullyFilled =
    props.emailUnsubmitted || props.isReset
      ? emailRegex.test(props.email)
      : !(
          props.username === "" ||
          password === "" ||
          firstName === "" ||
          lastName === "" ||
          !tosChecked ||
          (promptEmailVerify && verifyCode === "")
        );

  function handleSubmit() {
    if (!loading && !emailVerifySuccess) {
      setLoading(true);
      if (props.isReset) {
        axios
          .post(
            "/api/user/reset-password/start",
            {
              email: props.email,
            },
            {
              headers: { "X-CSRFToken": GetCSRFToken() },
            }
          )
          .then((res) => {
            setSuccess(true);
            setLoading(false);
            setCountDownInterval(
              setInterval(() => {
                setRedirectSeconds((s) => Math.max(0, s - 1));
              }, 1000)
            );
            setRedirectTimeout(
              setTimeout(() => {
                cleanUp();
              }, RedirectSecondsConst * 1000)
            );
          })
          .catch((err) => {
            if (process.env.NODE_ENV === "development") {
              console.log(err);
            }
          });
      } else if (props.emailUnsubmitted) {
        axios
          .post(
            "/api/user/email",
            { email: props.email.trim(), mobile: isMobile, registrationEnabled: props.registrationEnabled },
            { headers: { "X-CSRFToken": GetCSRFToken() } }
          )
          .then((res) => {
            if (res.data.includes("used")) {
              setEmailErrorMessage("This email is already in use. Please log in or reset your password.");
              setLoading(false);
            } else if (res.data.includes("waitlist")) {
              window.location.replace("https://usesalus.com/?redirectedFrom=registrationDisabled");
              setLoading(false);
            } else {
              if (isMobile) {
                // props.setEmailUnsubmitted(false);
                // setLoading(false);
                window.location.replace("https://usesalus.com/?redirectedFrom=mobileRegistration");
                setLoading(false);
              } else {
                props.setEmailUnsubmitted(false);
                props.setUsername(props.email);
                setLoading(false);
              }
            }
          })
          .catch((err) => {
            setEmailErrorMessage("An error occurred. Please try again or contact support.");
            setLoading(false);
          });
        // TODO: make sure no account exists with this email?
      } else if (promptEmailVerify) {
        axios
          .post(
            "/api/user/verify-email/complete",
            { code: verifyCode },
            {
              headers: { "X-CSRFToken": GetCSRFToken() },
            }
          )
          .then((res) => {
            setEmailVerifySuccess(true);
            props.setEmailVerified(true);
            setLoading(false);
            cleanUp();
          })
          .catch((err) => {
            setVerifyCode("");
            [
              {
                key: "code",
                fn: setVerifyCodeErrorMessage,
              },
            ].map((error_details_handlers) => error_details_handlers.fn(err.response.data[error_details_handlers.key]));
            setLoading(false);
          });
      } else {
        axios
          .post(
            "/api/user/register",
            {
              username: props.username.trim(),
              password: password,
              email: props.email,
              first_name: firstName.trim(),
              last_name: lastName.trim(),
            },
            { headers: { "X-CSRFToken": GetCSRFToken() } }
          )
          .then((res) => {
            setSuccess(true);
            setLoading(false);
            setServerUserName(res.data["name"]);
            setServerConfig(res.data["config"]);
            axios
              .get("/api/user/verify-email/start", {
                headers: { "X-CSRFToken": GetCSRFToken() },
              })
              .then((res) => {})
              .catch((err) => {
                if (process.env.NODE_ENV === "development") {
                  console.log(err);
                }
              });
          })
          .catch((err) => {
            setPassword("");
            if (err.response.status === 403) {
              if ("email" in err.response.data) {
                setEmailErrorMessage(err.response.data["email"]);
              } else if ("username" in err.response.data) {
                // If there is an email error, don't show the username error
                setUsernameErrorMessage(err.response.data["username"]);
              }
              if ("password" in err.response.data) {
                setPasswordRequirementsMessage(err.response.data["password"]);
              }
            } else {
              if (process.env.NODE_ENV === "development") {
                console.log(err);
              }
            }
            setLoading(false);
          });
        //TODO: limit username characters to only letters and numbers? Enforce 3 of 4 for a number/upper letter/lower letter/Symbol
      }
    }
  }

  return (
    <>
      <PagePaper
        title={
          props.isReset
            ? "Forgot Username or Password?"
            : promptEmailVerify
            ? "Welcome " + firstName.trim() + "!"
            : "Register (2 min)"
        }
        belowTitle={props.isReset || !props.emailUnsubmitted ? undefined : "Demystify your equity-based compensation."}
        belowDescription={
          props.isReset || !props.emailUnsubmitted
            ? undefined
            : "Trust Salus to gain confidence and automate managing your biggest asset in the constantly moving stock market with no fees. Register for free to join a growing community of users while saving thousands in taxes today."
        }
      >
        <FormControl
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
          }}
        >
          <Collapse in={!promptEmailVerify} sx={{ pt: promptEmailVerify ? 0 : 1 }}>
            <TextField
              variant="outlined"
              fullWidth
              disabled={!props.emailUnsubmitted || loading || success}
              value={props.email}
              error={emailErrorMessage !== undefined}
              onChange={(newValue) => {
                setEmailErrorMessage(undefined);
                props.setEmail(newValue.target.value);
              }}
              onKeyDown={catchEnterKeyDownToSubmit}
              inputRef={emailTextFieldRef}
              label="Email"
              type="email"
              autoComplete="email"
              autoFocus={props.emailUnsubmitted}
              helperText={emailErrorMessage}
              onFocus={() => {
                setEmailErrorMessage(undefined);
              }}
              onBlur={() => {
                if (!emailRegex.test(props.email)) {
                  setEmailErrorMessage("Please enter a valid email address");
                }
              }}
              inputProps={{ tabIndex: props.emailUnsubmitted ? 1 : -1 }}
              sx={{ pb: 2 }}
            />
          </Collapse>
          <Collapse in={props.isReset && success}>
            <Typography variant="body1" color="text.primary">
              If an account with this email exists, the username and instructions to reset the account password will be
              sent shortly.
              <br />
              <br />
              Redirecting in {redirectSeconds} second{redirectSeconds === 1 ? "" : "s"}...or click{" "}
              <Link
                onClick={() => {
                  if (redirectTimeout) {
                    clearTimeout(redirectTimeout);
                  }
                  cleanUp();
                }}
                sx={{ cursor: "pointer" }}
              >
                here
              </Link>{" "}
              to be redirected now.
            </Typography>
          </Collapse>
          <Collapse
            in={!props.emailUnsubmitted && !props.isReset && !promptEmailVerify}
            onEntering={() => {
              if (usernameTextFieldRef !== null && usernameTextFieldRef.current !== null) {
                usernameTextFieldRef.current.focus();
              }
            }}
            onExiting={() => {
              if (!promptEmailVerify && emailTextFieldRef !== null && emailTextFieldRef.current !== null) {
                emailTextFieldRef.current.focus();
              }
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                flex: 1,
                paddingTop: 1,
              }}
            >
              <TextField
                variant="outlined"
                fullWidth
                disabled={loading || success}
                error={usernameErrorMessage !== undefined}
                value={props.username}
                inputRef={usernameTextFieldRef}
                onChange={(newValue) => {
                  setUsernameErrorMessage(undefined);
                  props.setUsername(newValue.target.value);
                }}
                onKeyDown={catchEnterKeyDownToSubmit}
                label="Username"
                autoComplete="username"
                helperText={usernameErrorMessage}
                onBlur={() => {
                  if (props.username.length < 5) {
                    setUsernameErrorMessage("Username must be at least 5 characters");
                  }
                }}
                inputProps={{ tabIndex: props.emailUnsubmitted ? -1 : 1 }}
              />
              <TextField
                variant="outlined"
                type="password"
                autoComplete="current-password"
                fullWidth
                disabled={loading || success}
                error={passwordRequirementsMessage !== undefined}
                value={password}
                onChange={(newValue) => {
                  if (newValue.target.value !== "") {
                    setPasswordRequirementsMessage(undefined); // TODO: warn on the character minimum before hitting backend
                  }
                  setPassword(newValue.target.value);
                }}
                onKeyDown={catchEnterKeyDownToSubmit}
                autoFocus={!props.emailUnsubmitted} // TODO: this doesn't really work here?
                label="Password"
                helperText={passwordRequirementsMessage}
                onBlur={() => {
                  if (password.length < 8) {
                    setPasswordRequirementsMessage("Password must be at least 8 characters");
                  }
                }}
                inputProps={{ tabIndex: props.emailUnsubmitted ? -1 : 2 }}
              />
              {[
                {
                  value: firstName,
                  setter: setFirstName,
                  label: "First Name",
                  auto_complete: "given-name",
                },
                {
                  value: lastName,
                  setter: setLastName,
                  label: "Last Name",
                  auto_complete: "family-name",
                },
              ].map((item, index) => (
                <TextField
                  key={item.label}
                  variant="outlined"
                  fullWidth
                  value={item.value}
                  onChange={(newValue) => {
                    item.setter(newValue.target.value);
                  }}
                  disabled={loading || success}
                  onKeyDown={catchEnterKeyDownToSubmit}
                  label={item.label}
                  autoComplete={item.auto_complete}
                  inputProps={{ tabIndex: props.emailUnsubmitted ? -1 : 3 + index }}
                />
              ))}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={tosChecked}
                    onKeyDown={(e: KeyboardEvent) => {
                      if (e.key === "Enter") {
                        setTosChecked((old) => !old);
                      }
                    }}
                    onChange={() => setTosChecked((old) => !old)}
                    inputProps={{ tabIndex: props.emailUnsubmitted ? -1 : 5 }}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    I have read and agree to the{" "}
                    <Link href="/terms" target="_blank" rel="noreferrer" sx={{ textDecorationStyle: "dotted" }}>
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link href="/terms#privacy" target="_blank" rel="noreferrer" sx={{ textDecorationStyle: "dotted" }}>
                      Privacy Policy
                    </Link>
                    .
                  </Typography>
                }
              />
            </Box>
          </Collapse>
          <Collapse
            in={promptEmailVerify}
            onEntering={() => {
              if (emailVerifyCodeTextFieldRef !== null && emailVerifyCodeTextFieldRef.current !== null) {
                emailVerifyCodeTextFieldRef.current.focus();
              }
            }}
          >
            <Typography variant="body1" color="text.primary" sx={{ pb: 2 }}>
              You'll receive an email shortly with a verification code to enter below.
              <br />
              If you prefer, you can verify your email later and get started with a 10 minute tour now.
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              error={verifyCodeErrorMessage !== undefined}
              autoComplete="one-time-code"
              value={verifyCode}
              onChange={(newValue) => {
                setVerifyCodeErrorMessage(undefined);
                setVerifyCode(newValue.target.value);
              }}
              inputRef={emailVerifyCodeTextFieldRef}
              onKeyDown={catchEnterKeyDownToSubmit}
              label="Email Verification Code"
              helperText={verifyCodeErrorMessage}
              inputProps={{ tabIndex: promptEmailVerify ? -1 : 1 }}
            />
          </Collapse>
        </FormControl>
        <LoadingButton
          variant="contained"
          color={emailVerifySuccess ? "success" : undefined}
          endIcon={props.isReset ? <LockResetIcon /> : props.emailUnsubmitted ? <ArrowForwardIcon /> : undefined}
          startIcon={
            props.isReset || props.emailUnsubmitted ? undefined : promptEmailVerify ? (
              <MarkEmailReadOutlinedIcon />
            ) : (
              <PersonAddIcon />
            )
          }
          loading={loading}
          disabled={!FormFullyFilled}
          onClick={handleSubmit}
          tabIndex={props.emailUnsubmitted ? 2 : promptEmailVerify ? 2 : 6}
        >
          {props.isReset
            ? "Reset"
            : props.emailUnsubmitted
            ? "Get Started with Email"
            : promptEmailVerify
            ? "Verify Email"
            : "Create Account"}
        </LoadingButton>
        {props.isReset || props.emailUnsubmitted || !promptEmailVerify ? null : (
          <Button
            variant="text"
            startIcon={<LockResetIcon />}
            onClick={() =>
              axios
                .get("/api/user/verify-email/start", {
                  headers: { "X-CSRFToken": GetCSRFToken() },
                })
                .then((res) => {})
                .catch((err) => {
                  if (process.env.NODE_ENV === "development") {
                    console.log(err);
                  }
                })
            }
            disabled={loading || emailVerifySuccess}
            tabIndex={4}
          >
            Resend Code?
          </Button>
        )}
      </PagePaper>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {props.isReset ? (
          <Button
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setPassword("");
              props.openRegisterDialog();
            }}
            disabled={loading || success}
          >
            Create an account
          </Button>
        ) : props.emailUnsubmitted || promptEmailVerify ? null : (
          <Button
            startIcon={promptEmailVerify ? <LockResetIcon /> : <PersonIcon />}
            onClick={
              promptEmailVerify
                ? () =>
                    axios
                      .get("/api/user/verify-email/start", {
                        headers: { "X-CSRFToken": GetCSRFToken() },
                      })
                      .then((res) => {})
                      .catch((err) => {
                        if (process.env.NODE_ENV === "development") {
                          console.log(err);
                        }
                      })
                : () => {
                    setPassword("");
                    props.openLoginDialog();
                  }
            }
            disabled={loading || emailVerifySuccess}
            tabIndex={props.emailUnsubmitted ? 4 : promptEmailVerify ? 4 : 8}
          >
            {promptEmailVerify ? "Resend Code?" : "Already a user?"}
          </Button>
        )}
        {props.emailUnsubmitted ? null : (
          <Button
            color={promptEmailVerify ? "inherit" : "error"}
            variant={"text"}
            startIcon={promptEmailVerify ? null : <ArrowBackIcon />}
            endIcon={promptEmailVerify ? <ArrowForwardIcon /> : null}
            onClick={promptEmailVerify ? cleanUp : () => props.setEmailUnsubmitted(true)}
            disabled={loading || emailVerifySuccess}
            tabIndex={props.emailUnsubmitted ? 3 : promptEmailVerify ? 3 : 7}
          >
            {promptEmailVerify ? "Verify Later" : "Edit Email"}
          </Button>
        )}
      </Box>
    </>
  );
}

export default RegisterOrResetDialog;
